import { useState, useEffect } from 'react'
import { licenseService, companyService } from '../services/api'
import { Plus, Search, Edit, Trash2, Key, Copy, Download, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'

const Licenses = () => {
  const [licenses, setLicenses] = useState([])
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [companyFilter, setCompanyFilter] = useState('')
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total: 0,
    per_page: 10
  })
  const [showModal, setShowModal] = useState(false)
  const [showGenerateModal, setShowGenerateModal] = useState(false)
  const [editingLicense, setEditingLicense] = useState(null)
  const [formData, setFormData] = useState({
    device_id: '',
    company_id: 1,
    status: 'active',
    expiration_date: '',
    description: ''
  })
  const [generateData, setGenerateData] = useState({
    count: 1,
    company_id: 1,
    status: 'active',
    description: ''
  })

  useEffect(() => {
    loadLicenses()
    loadCompanies()
  }, [pagination.current_page, search, statusFilter, companyFilter])

  const loadLicenses = async () => {
    try {
      setLoading(true)
      const params = {
        page: pagination.current_page,
        limit: pagination.per_page,
        search,
        status: statusFilter,
        company_id: companyFilter
      }
      const response = await licenseService.getAll(params)
      console.log('Licencias recibidas:', response.data.licenses)
      setLicenses(response.data.licenses)
      setPagination(response.data.pagination)
    } catch (error) {
      toast.error('Error al cargar licencias')
    } finally {
      setLoading(false)
    }
  }

  const loadCompanies = async () => {
    try {
      const response = await companyService.getAll({ limit: 100 })
      setCompanies(response.data.companies)
    } catch (error) {
      console.error('Error al cargar empresas:', error)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      if (editingLicense) {
        await licenseService.update(editingLicense.id, formData)
        toast.success('Licencia actualizada exitosamente')
      } else {
        await licenseService.create(formData)
        toast.success('Licencia creada exitosamente')
      }
      setShowModal(false)
      setEditingLicense(null)
      resetForm()
      loadLicenses()
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar licencia')
    }
  }

  const handleGenerateMultiple = async (e) => {
    e.preventDefault()
    try {
      await licenseService.generateMultiple(generateData)
      toast.success(`${generateData.count} licencias generadas exitosamente`)
      setShowGenerateModal(false)
      resetGenerateForm()
      loadLicenses()
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al generar licencias')
    }
  }

  const handleEdit = (license) => {
    setEditingLicense(license)
    setFormData({
      device_id: license.device_id,
      company_id: license.company_id,
      status: license.status,
      expiration_date: license.expiration_date || '',
      description: license.description || ''
    })
    setShowModal(true)
  }

  const handleDelete = async (id) => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta licencia?')) return
    
    try {
      await licenseService.delete(id)
      toast.success('Licencia eliminada exitosamente')
      loadLicenses()
    } catch (error) {
      toast.error('Error al eliminar licencia')
    }
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('Device ID copiado al portapapeles')
  }

  const resetForm = () => {
    setFormData({
      device_id: '',
      company_id: 1,
      status: 'active',
      expiration_date: '',
      description: ''
    })
  }

  const resetGenerateForm = () => {
    setGenerateData({
      count: 1,
      company_id: 1,
      status: 'active',
      description: ''
    })
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', text: 'Activa' },
      inactive: { color: 'bg-gray-100 text-gray-800', text: 'Inactiva' },
      expired: { color: 'bg-red-100 text-red-800', text: 'Expirada' }
    }
    const config = statusConfig[status] || statusConfig.inactive
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    )
  }

  const isExpired = (expirationDate) => {
    if (!expirationDate) return false
    return new Date(expirationDate) < new Date()
  }

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Licencias</h1>
          <p className="text-gray-600">Gestiona las licencias del sistema</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowGenerateModal(true)}
            className="btn btn-secondary"
          >
            <Download className="h-4 w-4 mr-2" />
            Generar Múltiples
          </button>
          <button
            onClick={() => {
              setEditingLicense(null)
              resetForm()
              setShowModal(true)
            }}
            className="btn btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nueva Licencia
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="card mb-6">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar licencias..."
                  className="input pl-10"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input"
              >
                <option value="">Todos</option>
                <option value="active">Activa</option>
                <option value="inactive">Inactiva</option>
                <option value="expired">Expirada</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Empresa
              </label>
              <select
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
                className="input"
              >
                <option value="">Todas</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearch('')
                  setStatusFilter('')
                  setCompanyFilter('')
                  setPagination(prev => ({ ...prev, current_page: 1 }))
                }}
                className="btn btn-outline w-full"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Device ID</th>
                <th>Empresa</th>
                <th>Estado</th>
                <th>Expiración</th>
                <th>Descripción</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  </td>
                </tr>
              ) : licenses.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-gray-500">
                    No se encontraron licencias
                  </td>
                </tr>
              ) : (
                licenses.map(license => (
                  <tr key={license.id} className="hover:bg-gray-50">
                    <td>
                      <div className="flex items-center">
                        <Key className="h-4 w-4 mr-2 text-gray-400" />
                        <span className="font-mono text-sm font-medium text-gray-900">
                          {license.device_id}
                        </span>
                        <button
                          onClick={() => copyToClipboard(license.device_id)}
                          className="ml-2 text-gray-400 hover:text-gray-600"
                          title="Copiar device_id"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="text-sm text-gray-900">
                        {license.Company?.name || 'Sin empresa'}
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(license.status)}
                        {license.expiration_date && isExpired(license.expiration_date) && (
                          <span className="text-xs text-red-600">Expirada</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="text-sm text-gray-900">
                        {license.expiration_date ? (
                          <span className={isExpired(license.expiration_date) ? 'text-red-600' : ''}>
                            {new Date(license.expiration_date).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-gray-500">Sin expiración</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {license.description || '-'}
                      </div>
                    </td>
                    <td>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(license)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(license.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Eliminar"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {pagination.total_pages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Mostrando {((pagination.current_page - 1) * pagination.per_page) + 1} a{' '}
                {Math.min(pagination.current_page * pagination.per_page, pagination.total)} de{' '}
                {pagination.total} resultados
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
                  disabled={pagination.current_page === 1}
                  className="btn btn-outline btn-sm disabled:opacity-50"
                >
                  Anterior
                </button>
                <span className="px-3 py-2 text-sm text-gray-700">
                  Página {pagination.current_page} de {pagination.total_pages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
                  disabled={pagination.current_page === pagination.total_pages}
                  className="btn btn-outline btn-sm disabled:opacity-50"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Licencia */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingLicense ? 'Editar Licencia' : 'Nueva Licencia'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Device ID</label>
                  <input
                    type="text"
                    value={formData.device_id}
                    onChange={(e) => setFormData({ ...formData, device_id: e.target.value })}
                    className="input font-mono"
                    maxLength="7"
                    placeholder="Dejar vacío para generar automáticamente"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Empresa</label>
                  <select
                    value={formData.company_id}
                    onChange={(e) => setFormData({ ...formData, company_id: parseInt(e.target.value) })}
                    className="input"
                    required
                  >
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Estado</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="input"
                    required
                  >
                    <option value="active">Activa</option>
                    <option value="inactive">Inactiva</option>
                    <option value="expired">Expirada</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Fecha de Expiración</label>
                  <input
                    type="date"
                    value={formData.expiration_date}
                    onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Descripción</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input"
                    rows="3"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn btn-outline"
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingLicense ? 'Actualizar' : 'Crear'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Generación Múltiple */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Generar Múltiples Licencias
              </h3>
              <form onSubmit={handleGenerateMultiple} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Cantidad</label>
                  <input
                    type="number"
                    value={generateData.count}
                    onChange={(e) => setGenerateData({ ...generateData, count: parseInt(e.target.value) })}
                    className="input"
                    min="1"
                    max="100"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Empresa</label>
                  <select
                    value={generateData.company_id}
                    onChange={(e) => setGenerateData({ ...generateData, company_id: parseInt(e.target.value) })}
                    className="input"
                    required
                  >
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Estado</label>
                  <select
                    value={generateData.status}
                    onChange={(e) => setGenerateData({ ...generateData, status: e.target.value })}
                    className="input"
                    required
                  >
                    <option value="active">Activa</option>
                    <option value="inactive">Inactiva</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Descripción</label>
                  <textarea
                    value={generateData.description}
                    onChange={(e) => setGenerateData({ ...generateData, description: e.target.value })}
                    className="input"
                    rows="3"
                    placeholder="Descripción opcional para todas las licencias"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowGenerateModal(false)}
                    className="btn btn-outline"
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Generar {generateData.count} Licencias
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Licenses 